import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";

const PROVIDES = [
  "Professional wrestling ring",
  "Professional wrestling talent",
  "Referees",
  "Announcers when appropriate",
  "Talent booking",
  "Show coordination",
  "Setup and breakdown",
  "Customizable match cards",
  "Family-friendly presentation",
];

const CLIENTS = [
  "Dealerships",
  "Motorcycle dealerships",
  "Fairs",
  "Festivals",
  "Breweries",
  "Businesses",
  "Community events",
  "Shopping centers",
  "Customer appreciation events",
  "Private events",
  "Indoor and outdoor venues",
];

export default function WrestlingPage() {
  return (
    <Layout padTop>
      <Seo
        title="Professional Wrestling Shows in North Carolina | Hustle Promotions"
        description="Book professional wrestling for your North Carolina venue, fair, festival or event. Hustle Promotions provides the ring, talent, referees and full show coordination."
      />

      {/* HERO */}
      <section data-testid="wrestling-hero" style={{ background: "var(--black)" }}>
        <div className="container-editorial section-pad">
          <div className="split-hero">
            <div>
              <Reveal>
                <p className="eyebrow" style={{ marginBottom: "1.5rem" }}>
                  Professional Wrestling — North Carolina
                </p>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="h-page" data-testid="wrestling-h1" style={{ maxWidth: "17ch" }}>
                  Bring Professional Wrestling To Your North Carolina Venue.
                </h1>
              </Reveal>
              <Reveal delay={140}>
                <p className="lede" data-testid="wrestling-nc-statement" style={{ marginTop: "1.75rem" }}>
                  Professional wrestling bookings are currently available within North Carolina only.
                </p>
              </Reveal>
              <Reveal delay={200}>
                <div className="flex flex-col sm:flex-row sm:flex-wrap" style={{ gap: "1rem", marginTop: "2.5rem" }}>
                  <Link to="/book" className="btn btn-solid" data-testid="wrestling-book-cta">
                    Book A Wrestling Show <ArrowRight size={16} strokeWidth={2} />
                  </Link>
                  <Link to="/book" className="btn btn-outline" data-testid="wrestling-info-cta">
                    Request Wrestling Information
                  </Link>
                </div>
              </Reveal>
            </div>
            <Reveal delay={120}>
              <div className="media-frame media-tall">
                <img
                  src="/assets/wrestling-champion-raise.jpg"
                  alt="A referee raising a professional wrestler's arm as the champion holds a title belt, black and white"
                  className="img-cover"
                  style={{ objectPosition: "70% 50%" }}
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WOMEN'S WRESTLING — PROMINENT FULL-WIDTH FEATURE */}
      <section data-testid="wrestling-womens" className="relative">
        <div className="hairline" />
        <div className="media-band">
          <img
            src="/assets/wrestling-womens-strike.webp"
            alt="A women's professional wrestler delivering a strike to an opponent as a referee watches, black and white"
            className="img-cover obj-left-bottom"
            style={{ position: "absolute", inset: 0 }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 38%, rgba(0,0,0,0.78) 100%)",
            }}
          />
          <div
            className="container-editorial relative"
            style={{ height: "100%", display: "flex", alignItems: "flex-end", paddingTop: "2rem", paddingBottom: "clamp(2rem, 5vw, 3.5rem)" }}
          >
            <Reveal>
              <div>
                <p className="eyebrow" style={{ marginBottom: "0.85rem" }}>Every Card</p>
                <h2 className="h-section" style={{ maxWidth: "18ch" }}>
                  Women's Wrestling, Front And Center.
                </h2>
              </div>
            </Reveal>
          </div>
        </div>
        <div className="hairline" />
      </section>

      {/* WHAT WE PROVIDE */}
      <section data-testid="wrestling-provides" style={{ background: "var(--off-black)" }}>
        <div className="container-editorial section-pad">
          <div className="split-hero" style={{ alignItems: "start" }}>
            <div>
              <Reveal>
                <p className="eyebrow">What Hustle Promotions Provides</p>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="h-section" style={{ maxWidth: "12ch", marginTop: "1rem" }}>
                  The Full Show.
                </h2>
              </Reveal>
              <Reveal delay={140}>
                <p className="body-md" style={{ maxWidth: "44ch", marginTop: "1.5rem" }}>
                  From the ring to the final match, Hustle Promotions handles everything a professional
                  wrestling show requires.
                </p>
              </Reveal>
            </div>
            <div className="spec-grid cols-2" data-testid="wrestling-provides-list">
              {PROVIDES.map((item, i) => (
                <Reveal key={item} delay={i * 40}>
                  <div className="spec-item">
                    <span className="spec-mark">{String(i + 1).padStart(2, "0")}</span>
                    <span className="spec-text">{item}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ACTION RHYTHM */}
      <section data-testid="wrestling-action" style={{ background: "var(--black)" }}>
        <div className="container-editorial section-pad">
          <Reveal>
            <p className="eyebrow" style={{ marginBottom: "1.75rem" }}>In The Ring</p>
          </Reveal>
          <div className="duo-grid">
            <Reveal>
              <div className="media-frame media-wide">
                <img
                  src="/assets/wrestling-airborne-beard.jpg"
                  alt="A bearded professional wrestler leaping off the ropes with both arms raised, black and white"
                  className="img-cover obj-center-top"
                />
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="media-frame media-wide">
                <img
                  src="/assets/wrestling-carry-hold.jpg"
                  alt="A professional wrestler holding an opponent horizontally across his body near the ropes, black and white"
                  className="img-cover obj-center-bottom"
                />
              </div>
            </Reveal>
          </div>
          <Reveal delay={120}>
            <div className="media-frame media-wide" style={{ marginTop: "clamp(0.75rem, 1.5vw, 1rem)" }}>
              <img
                src="/assets/wrestling-strike.jpg"
                alt="Two professional wrestlers mid-strike in the ring, black and white"
                className="img-cover obj-center-top"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CLIENT TYPES */}
      <section data-testid="wrestling-clients" style={{ background: "var(--off-black)" }}>
        <div className="hairline" />
        <div className="container-editorial section-pad">
          <div className="split-hero" style={{ alignItems: "start" }}>
            <div>
              <Reveal>
                <p className="eyebrow">Who We Work With</p>
              </Reveal>
              <Reveal delay={80}>
                <h2 className="h-section" style={{ maxWidth: "14ch", marginTop: "1rem" }}>
                  Where The Show Fits.
                </h2>
              </Reveal>
              <Reveal delay={140}>
                <p className="body-md" style={{ maxWidth: "44ch", marginTop: "1.5rem" }}>
                  Hustle Promotions builds professional wrestling shows for a wide range of clients,
                  including but not limited to:
                </p>
              </Reveal>
            </div>
            <div className="spec-grid cols-2" data-testid="wrestling-clients-list">
              {CLIENTS.map((c) => (
                <Reveal key={c} delay={30}>
                  <div className="spec-item">
                    <span className="spec-mark">—</span>
                    <span className="spec-text">{c}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="wrestling-cta" style={{ background: "var(--black)" }}>
        <div className="hairline" />
        <div className="container-editorial section-pad">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between" style={{ gap: "2rem" }}>
            <Reveal>
              <h2 className="h-section" style={{ maxWidth: "18ch" }}>
                Put Professional Wrestling On Your Calendar.
              </h2>
              <p
                className="font-cond"
                data-testid="wrestling-nc-cities"
                style={{ textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "0.82rem", color: "var(--gray)", maxWidth: "44ch", marginTop: "1.5rem", lineHeight: 1.7 }}
              >
                From Charlotte and the Lake Norman area to Raleigh, Greensboro, and Winston-Salem — professional wrestling shows across North Carolina.
              </p>
            </Reveal>
            <Reveal delay={100}>
              <div className="flex flex-col sm:flex-row sm:flex-wrap" style={{ gap: "1rem" }}>
                <Link to="/book" className="btn btn-solid" data-testid="wrestling-cta-book">
                  Book A Wrestling Show <ArrowRight size={16} strokeWidth={2} />
                </Link>
                <Link to="/book" className="btn btn-outline" data-testid="wrestling-cta-info">
                  Request Wrestling Information
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </Layout>
  );
}
